import React from 'react';

export type ComponentSize = 'small' | 'medium' | 'large' | 'xsmall' | 'default';
export type TagVariant = 'icon-only' | 'text-only' | 'icon-text' | 'date' | 'compact';
export type ButtonVariant = 'icon' | 'text' | 'text-icon';

export interface SkeletonProps {
  width: string;
  height: string;
  borderRadius?: string;
  animation?: 'pulse' | 'shimmer' | 'wave';
  style?: React.CSSProperties;
}

/**
 * Calculate skeleton dimensions for tag components
 */
export function getTagSkeletonProps(
  variant: TagVariant,
  size: ComponentSize = 'medium',
  compact: boolean = false
): SkeletonProps {
  let skeletonWidth = '60px'; // Default for text-only
  let skeletonHeight = compact ? '24px' : '32px';
  
  // Adjust for size
  if (size === 'small') {
    skeletonHeight = '24px';
  } else if (size === 'large') {
    skeletonHeight = '40px';
  }
  
  // Adjust for variant
  if (variant === 'icon-only' || variant === 'compact') {
    skeletonWidth = skeletonHeight; // Square for icon-only
  } else if (variant === 'icon-text') {
    skeletonWidth = '80px';
  }
  
  return {
    width: skeletonWidth,
    height: skeletonHeight,
    borderRadius: '16px',
    animation: 'pulse'
  };
}

/**
 * Calculate skeleton dimensions for button components
 */
export function getButtonSkeletonProps(
  variant: ButtonVariant,
  size: ComponentSize = 'default'
): SkeletonProps {
  let skeletonHeight = '48px'; // Default height
  let skeletonWidth = '120px'; // Default width
  
  // Adjust for size
  switch (size) {
    case 'xsmall':
      skeletonHeight = '32px';
      skeletonWidth = '80px';
      break;
    case 'small':
      skeletonHeight = '40px';
      skeletonWidth = '100px';
      break;
    case 'medium':
      skeletonHeight = '32px';
      skeletonWidth = '80px';
      break;
    case 'large':
      skeletonHeight = '48px';
      skeletonWidth = '120px';
      break;
    default:
      skeletonHeight = '48px';
      skeletonWidth = '120px';
  }
  
  // For icon-only buttons, make it square
  if (variant === 'icon') {
    skeletonWidth = skeletonHeight;
  } else if (variant === 'text-icon') {
    // Slightly wider for text + icon
    skeletonWidth = size === 'small' ? '80px' : '120px';
  }
  
  return {
    width: skeletonWidth,
    height: skeletonHeight,
    borderRadius: '4px',
    animation: 'pulse'
  };
}

/**
 * Calculate skeleton dimensions for secondary/tertiary tags
 */
export function getCompactTagSkeletonProps(
  iconOnly: boolean = false,
  size: 'small' | 'medium' = 'medium'
): SkeletonProps {
  const width = iconOnly ? '26px' : '80px';
  const height = size === 'small' ? '22px' : '26px';
  
  return {
    width,
    height,
    borderRadius: '16px',
    animation: 'pulse'
  };
}

/**
 * Calculate skeleton dimensions for state tags
 */
export function getStateTagSkeletonProps(
  iconOnly: boolean = false,
  size: ComponentSize = 'small'
): SkeletonProps {
  let skeletonHeight = '20px';
  let skeletonWidth = iconOnly ? '20px' : '60px';
  
  switch (size) {
    case 'small':
      skeletonHeight = '20px';
      skeletonWidth = iconOnly ? '20px' : '60px';
      break;
    case 'medium':
      skeletonHeight = '24px';
      skeletonWidth = iconOnly ? '24px' : '80px';
      break;
    case 'large':
      skeletonHeight = '28px';
      skeletonWidth = iconOnly ? '28px' : '100px';
      break;
  }
  
  return {
    width: skeletonWidth,
    height: skeletonHeight,
    borderRadius: '4px',
    animation: 'pulse'
  };
}

